import { useDroppable } from '@dnd-kit/core';
import type { FilterNode } from '@ofmos/shared/filter-ast';
import type { OrderBy } from '@ofmos/shared/record-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FieldMetadata, ObjectMetadata } from '../../../data/client';
import { pagedIds, useInfiniteRecordList, useRecord } from '../../../data/hooks';
import { TagDot } from '../../../fields/display/TagDot';
import { Button, cx, IconButton, Skeleton, type TagTone } from '../../../ui';
import { CreateRecordDialog } from '../CreateRecordDialog';
import { usePresentRows } from '../table/usePresentRows';
import type { BoardColumn } from './boardColumns';
import { columnDot, columnFilter } from './boardColumns';
import { KanbanColumnFrame } from './frames';
import { KanbanCard } from './KanbanCard';
import { columnDropId } from './moveTarget';

/**
 * One column: its own QUERY, its own count, its own cursor (D21).
 *
 * A COMPONENT PER COLUMN rather than a loop of hooks in the board, and that is a hard
 * requirement rather than a preference: the column set is dynamic (it is the group
 * field's options, which an admin edits), so a `useInfiniteRecordList` per column
 * written as a loop would break the rules of hooks the first time somebody adds a
 * select option. `useQueries` was the alternative and it cannot page.
 *
 * WHAT THE COUNT NOW MEANS, AND WHERE IT COMES FROM (group-by-aggregation.md D23). It is
 * the size of this column's whole set, not of the loaded page - the old board counted the
 * cards it happened to have fetched out of one 200-row page, which is the spreadsheet
 * feeling in a new costume. But it no longer comes from THIS query: it is handed down from
 * one grouped read for the whole board. Each column used to ask for `withTotalCount`, and
 * `withTotalCount` is a second full scan of the filter, so N options meant N extra scans;
 * one grouped statement returns all N counts in one.
 *
 * TWO THINGS FOLLOW, and the second is the one that makes this more than a performance
 * note. One cache entry carries the counts instead of N, so a drag invalidates one entry
 * instead of N. And the column's row list stops PUBLISHING a count, which means
 * `optimisticList.ts` stops invalidating it wholesale: the source column of a drag now
 * takes the cheap splice path instead of a refetch. The destination column still
 * invalidates, because there is genuinely no insertion path.
 *
 * WHAT THE COUNT AND THE CARDS DO NOT SHARE is a snapshot, and that is deliberate rather
 * than overlooked: the header comes from one repeatable-read grouped read while the cards
 * come from this ordinary query. A header of 12 over a page fetched a moment earlier has
 * never been a parts-and-whole claim, and today's `withTotalCount` was already a second
 * untransacted statement, so this is not a regression either.
 *
 * Mobile behaviour lives here, not in a media-query afterthought. The column is a snap
 * stop that takes most of the screen, it scrolls VERTICALLY on its own, and its header
 * sticks while it does - so the answer to "which column am I looking at?" is always on
 * screen. The column is also focusable: a scrollable region that cannot be reached by
 * keyboard is not reachable at all.
 */
const COLUMN_PAGE_SIZE = 25;

export interface KanbanColumnProps {
  readonly column: BoardColumn;
  /**
   * The object itself, not only its id: the column's own create names it ("New deal in
   * Qualified") and hands it to the create dialog.
   */
  readonly object: ObjectMetadata;
  /**
   * THE VIEW'S FIELDS, which is where a card's row menu resolves its stage submenu from
   * (wave 5 round 4, B4; the table hands `RecordRowMenu` the same list for the same reason -
   * round 2's finding F2, a menu that read every field of the object offered "Set Language"
   * on a view showing Status).
   */
  readonly fields: readonly FieldMetadata[];
  /**
   * THE FIELD, not its id, for the same reason: a create in this column prefills the
   * group field, and the dialog shows that value as the option chip it will become.
   */
  readonly groupField: FieldMetadata;
  /** The VIEW's filter. The column's own condition is ANDed onto it. */
  readonly viewFilter: FilterNode | undefined;
  readonly select: readonly string[];
  readonly orderBy: readonly OrderBy[] | undefined;
  readonly labelField: FieldMetadata | undefined;
  readonly secondaryFields: readonly FieldMetadata[];
  /**
   * This column's size, out of the board's ONE grouped read (D23). `undefined` while that
   * read is in flight, and the header then shows no number rather than the loaded page's
   * length: a count of whatever happens to have been fetched is precisely the number D21
   * deleted.
   */
  readonly count: number | undefined;
  readonly onOpen: (recordId: string) => void;
  /**
   * Reports the loaded ids up, so the board can assemble `columnOfRecord` for the drop
   * path. The board cannot ask the columns - each one owns its own query - and the drop
   * path's contract is deliberately unchanged (D21), so the ids have to travel.
   */
  readonly onIdsChange: (columnId: string, ids: readonly string[]) => void;
  /** A record was created in this column. The board decides where that lands. */
  readonly onCreated: (recordId: string) => void;
}

export function KanbanColumn({
  column,
  object,
  fields,
  groupField,
  viewFilter,
  select,
  orderBy,
  labelField,
  secondaryFields,
  count,
  onOpen,
  onIdsChange,
  onCreated,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: columnDropId(column.id) });
  const objectId = object.id;
  const [createOpen, setCreateOpen] = useState(false);

  const filter = useMemo(
    () => columnFilter(column, groupField.id, viewFilter),
    [column, groupField.id, viewFilter],
  );

  const list = useInfiniteRecordList(objectId, {
    select,
    filter,
    orderBy,
    limit: COLUMN_PAGE_SIZE,
    enabled: select.length > 0,
  });

  const ids = useMemo(() => pagedIds(list.data) ?? [], [list.data]);

  /**
   * THE CARDS THIS COLUMN STILL DRAWS, WHICH IS NOT THE SAME LIST AS THE ONE IT HOLDS (wave 5
   * round 4, M3). A record that has left this column keeps its slot until its card's fade has
   * run - the Row family's other half - and the hook that holds it is the filter strip's,
   * `usePresentRows`, so the board and the list answer "is this row still here?" the same way.
   *
   * WHAT IS REPORTED UP IS THE LIVE LIST AND NOT THE RETAINED ONE. `onIdsChange` feeds the
   * board's `columnOfRecord` map, which is what the drop path resolves a target against: a
   * card that is merely finishing its exit would otherwise be claimed by two columns at once,
   * and the very next drop would be measured against the column the record just left.
   */
  const cards = usePresentRows(ids);

  useEffect(() => {
    onIdsChange(column.id, ids);
  }, [column.id, ids, onIdsChange]);

  const countLabel = count === undefined ? '' : `: ${count} ${count === 1 ? 'record' : 'records'}`;

  /** The header's dot and every card's dot in this column are one answer (`columnDot`). */
  const dot = columnDot(column, groupField);

  return (
    <KanbanColumnFrame
      ref={setNodeRef}
      // A scrolling region that cannot be focused cannot be scrolled by keyboard,
      // so a column taller than the screen would hide its own cards.
      // (The frame renders the `<section>`; the tab stop is a scroll container's, not a widget's.)
      tabIndex={0}
      aria-label={`${column.label}${countLabel}`}
      /* THE FRAME IS THE BOARD'S SHARED COLUMN (`frames.tsx`, BR-PARTS): the sunken rounded
         box, its hairline, and the accent while a card is over it. What follows is this
         board's own: the phone snap stop, the scroll rules and the 320 px desktop width. */
      highlighted={isOver}
      className={cx(
        'board-column w-[85vw] snap-start',
        // A column scrolls itself rather than stretching the page: that is what
        // keeps the sticky header below in view. 70dvh leaves the page header and
        // the bottom nav visible on a phone; short columns stay short.
        //
        // `relative` IS WHAT MAKES THAT TRUE (audit P2-5). An absolutely positioned
        // descendant whose containing block sits outside a scroller is not clipped by
        // it, and every card with an empty value carries one: `EmptyValue` renders a
        // visually hidden "empty", and `.sr-only` is `position: absolute`. Without a
        // containing block here those 1px spans reported their place in the board's
        // scrolled coordinates to the DOCUMENT - measured at 375px, the page scrolled
        // 743px sideways, taking the header and the tab bar with it, and stood 1132px
        // tall for 812px of content. With it: 0 and 957.
        'relative max-h-[70dvh] min-h-40 overflow-y-auto',
        /* AND FROM THE DESKTOP BREAKPOINT UP THE COLUMN OWNS NO SCROLL AT ALL (wave 5
           round 3, C3-3). The board adopted `PaneHost`, and that host's first rule is that
           it is the screen's ONE vertical scrollport (`docs/decisions/ui-foundation-w5.md`,
           A0): a column that kept its own scroller would put the cards in a box that never
           reaches the window toolbar, which is the whole of the owner's "kein Liquid Glass"
           - a bar with nothing moving under it is a band of paint. So above `md:` the
           column is as tall as its cards and the host scrolls them, exactly as the table's
           rows scroll inside the same host. The phone keeps the 70dvh cap and its own
           scroller: there the columns are snap stops the width of the screen, the host is
           `display: block`, and the page is the scroller it always was. */
        'md:max-h-none md:overflow-y-visible',
        /* 320px FROM THE DESKTOP BREAKPOINT UP (wave 5 round 4, B4). `w-72` is 288, and the
           spec's board column is 320 (`--board-column-width`); nothing in the file said so,
           which is how a Tailwind step became the geometry. The phone keeps 85vw, because
           there a column is a snap stop the width of the screen. */
        'md:w-(--board-column-width) md:snap-align-none',
      )}
    >
      {/* STICKY ONLY WHERE THE COLUMN IS THE SCROLLER (C3-3). Above `md:` the column no
          longer scrolls, so `sticky` would resolve against the PANE HOST instead - where
          the board's own pinned line already stands - and eight column headers would pile
          onto one line under it. */}
      {/* THE HEADER IS A 40px ROW AND ITS LABEL IS NOT A CAPSULE (wave 5 round 4, B4).
          It drew the column's option as `Badge tone={column.tone}`, which is the one thing the
          quality contract's legacy list names by its use rather than by its name: a Badge for a
          VALUE. The option IS a value - it is what the group field holds - and the product
          settled its read-surface form in P4 F1: text with a coloured dot in front of it, never
          a filled capsule (`fields/display/TagDot.tsx`, Finder's own construction). So the
          header now says what a table cell for the same field says.
          The height is the kit's plain form row: kit: Apple macOS 27 UI Kit.sketch /
          Forms/Example Forms/Light, Window/Form/Row frame 440x40 (`--board-column-head`). */}
      <header className="board-column-head sticky top-0 border-b border-line-subtle bg-surface-sunken md:static">
        <span className="board-column-label">
          {dot !== null && <TagDot tone={dot} />}
          <span className="truncate text-sm font-medium text-ink">{column.label}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <span className="text-xs tabular-nums text-ink-muted">{count ?? ''}</span>
          {/**
           * THE COLUMN'S OWN CREATE (audit P2-7, inventory row 7). "Add a lead to this
           * stage" is what a pipeline board is for, and this board had no create at all -
           * the only way to put a record in a column was to make it somewhere else and
           * drag it here.
           *
           * IN THE HEADER, which is STICKY: a column caps itself at 70dvh and scrolls, so
           * a create at the foot would be below the fold in exactly the full columns
           * somebody is working in. Drawn at 32px and PRESSED at 40 through `.hit-expand`,
           * the recipe this repository already keeps for a control that must not grow the
           * row it sits in.
           *
           * NOT ON THE NO-VALUE COLUMN. Its records are the ones with no value in this
           * field, and "create with no stage" is what the list's own New already does -
           * offering it here would be offering the invisible record the audit is about.
           */}
          {column.optionId !== null && (
            <IconButton
              icon="plus"
              size="sm"
              className="hit-expand"
              label={`New ${object.labelSingular.toLowerCase()} in ${column.label}`}
              onClick={() => setCreateOpen(true)}
            />
          )}
        </span>
      </header>

      {column.optionId !== null && (
        <CreateRecordDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          object={object}
          prefill={[{ field: groupField, value: column.optionId }]}
          onCreated={onCreated}
        />
      )}

      {/* The cards' gap and the column's own padding are the board's one rhythm
          (`--board-gap`, 8px): doc: spec/screens/list.md section 2 item 12, "8 px gaps". */}
      <div className="flex flex-1 flex-col gap-(--board-gap) p-(--board-gap)">
        {list.isPending ? (
          <div aria-busy="true" className="space-y-2">
            <Skeleton shape="block" className="h-16" label={`Loading ${column.label}`} />
            <Skeleton shape="block" className="h-16" />
          </div>
        ) : list.isError ? (
          <p role="alert" className="px-2 py-4 text-xs text-danger">
            {`This column could not be loaded. ${list.error.message}`}
          </p>
        ) : cards.ids.length === 0 ? (
          <p className="px-2 py-8 text-center text-xs text-ink-subtle">No records</p>
        ) : (
          cards.ids.map((recordId) => (
            <KanbanColumnCard
              key={recordId}
              object={object}
              fields={fields}
              groupFieldId={groupField.id}
              objectId={objectId}
              recordId={recordId}
              labelField={labelField}
              secondaryFields={secondaryFields}
              tone={dot}
              onOpen={onOpen}
              present={cards.present(recordId)}
              onExited={() => cards.exited(recordId)}
            />
          ))
        )}

        {list.hasNextPage === true && (
          <Button
            variant="ghost"
            size="sm"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            {count === undefined
              ? `Show more (${ids.length} loaded)`
              : `Show more (${ids.length} of ${count})`}
          </Button>
        )}
      </div>
    </KanbanColumnFrame>
  );
}

/**
 * One card, resolved from its CANONICAL cache entry (INV-13) rather than handed down as
 * an object. That is what makes an inline edit elsewhere re-render this card and not
 * the column, and it is why the column query stores ids.
 */
function KanbanColumnCard({
  object,
  fields,
  groupFieldId,
  objectId,
  recordId,
  labelField,
  secondaryFields,
  tone,
  onOpen,
  present,
  onExited,
}: {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly groupFieldId: string;
  readonly objectId: string;
  readonly recordId: string;
  readonly labelField: FieldMetadata | undefined;
  readonly secondaryFields: readonly FieldMetadata[];
  readonly tone: TagTone | null;
  readonly onOpen: (recordId: string) => void;
  readonly present: boolean;
  readonly onExited: () => void;
}) {
  const record = useRecord(objectId, recordId);
  /**
   * A RETAINED CARD WITH NOTHING TO DRAW RELEASES ITS SLOT ITSELF. The exit is `useMotion`'s
   * and `useMotion` lives in `KanbanCard`, which this branch never renders - so a record that
   * left the column in the same turn its canonical entry went missing would hold a skeleton in
   * the column for the rest of the session. The ref is what keeps that to ONE report:
   * `usePresentRows.exited` builds a new array on every call, so a second one would re-render
   * forever.
   */
  const released = useRef(false);
  const missing = record.data === undefined || record.data === null;
  useEffect(() => {
    if (missing && !present && !released.current) {
      released.current = true;
      onExited();
    }
  });
  if (record.data === undefined || record.data === null) {
    return present ? <Skeleton shape="block" className="h-16" /> : null;
  }
  return (
    <KanbanCard
      object={object}
      fields={fields}
      groupFieldId={groupFieldId}
      objectId={objectId}
      record={record.data}
      labelField={labelField}
      secondaryFields={secondaryFields}
      tone={tone}
      onOpen={onOpen}
      present={present}
      onExited={onExited}
    />
  );
}
